import {NxButton, NxButtonVariant, NxLoader} from '@nextbank/ui-components';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import React, {ReactElement, useContext, useEffect, useMemo} from 'react';
import {Trans} from 'react-i18next';
import {useParams} from 'react-router';
import {ReactComponent as PrintIcon} from '../../../../../assets/images/icon-print.svg';
import {PhaseName} from '../../../../../constants/api-urls';
import {UrlParams} from '../../../../../routes/routes.model';
import usePost from '../../../../../shared/hooks/use-post.hook';
import useSingleQuery from '../../../../../shared/hooks/use-single-query.hook';
import {Phase, PhaseType, PhaseWithStaticFields} from '../../../../../shared/model/phase.model';
import {PrintType} from '../../../../../shared/model/print.model';
import {ApiHelper} from '../../../../../utils/api-helper';
import {TransHelper} from '../../../../../utils/trans-helper';
import {SystemContext} from '../../../../shared/system-context-provider/SystemContextProvider';
import {LoanApplicationContext} from '../../LoanApplication';
import LoanApplicationStep from '../shared/loan-application-step/LoanApplicationStep';
import usePrint from '../shared/loan-application-step/print-button/usePrint';
import ReleaseButton from './release-button/ReleaseButton';
import styles from './Summary.module.scss';
import { StaticFieldsHelper } from '../../../../../utils/step-form-utils/static-fields-helper';
import { LOAN_APPLICATION_FIELD_CODES } from '../../../../../shared/model/step-forms/loan-application-data-form.model';
import { StringHelper } from '../../../../../utils/string-helper';
import {LoanCreationType} from '../../../../../shared/model/creation-type.model';

export const TRANS_PREFIX = 'LOAN_APPLICATIONS.SUMMARY';
export const PrefixTrans = TransHelper.getPrefixedTrans(TRANS_PREFIX);
const {getApplicationUrl, getPhaseConfigurationUrl} = ApiHelper;

interface StepRequiredData {
  config: Phase;
}

export default function Summary(): ReactElement {

  const {processId, setOpenedPhaseId, application, process} = useContext(LoanApplicationContext);
  const {printsEnabled} = useContext(SystemContext);
  const phaseUrl = getPhaseConfigurationUrl(processId, PhaseName.SUMMARY);
  const config = useSingleQuery<Phase>(phaseUrl);
  const {applicationId} = useParams<UrlParams>();

  const loanApplicationPhaseUrl = getPhaseConfigurationUrl(processId, PhaseName.LOAN_APPLICATION_DATA);
  const loanApplicationConfig = useSingleQuery<PhaseWithStaticFields>(loanApplicationPhaseUrl);

  const isNewLoanCreationType = useMemo(() => {
    const loanApplicationDataPhase = process.phases.find(phase => phase.type === PhaseType.LOAN_APPLICATION_DATA);

    if (application?.simulations && loanApplicationDataPhase) {
      const loanApplicationDataSimulation = application.simulations.find(simulation => simulation.phaseId === loanApplicationDataPhase.id);
      return loanApplicationDataSimulation?.input.creationType === LoanCreationType.NEW_LOAN
    }
    return false;
  }, [JSON.stringify(process)]);
  
  const staticFields = loanApplicationConfig?.staticFields ?? [];
  const withLinkedDepositAccountField = StaticFieldsHelper.findField(staticFields, LOAN_APPLICATION_FIELD_CODES.withLinkedDepositAccount);

  useEffect(() => {
    if (config) {
      setOpenedPhaseId(config.id);
    }
  }, [config]);
  
  const SummaryStep = ({config}: StepRequiredData): ReactElement => {

    const {onPrint, isGenerating} = usePrint(parseInt(applicationId), config.id, PrintType.SUMMARY);
    const submitApplication = usePost(`${getApplicationUrl(applicationId)}/submit`);

    return <LoanApplicationStep stepNavigation={
                                  <ReleaseButton submitApplicationRequest={submitApplication} 
                                                 withLinkedDepositAccount={StringHelper.stringToBoolean(withLinkedDepositAccountField?.defaultValue)}
                                                 isNewLoanCreationType={isNewLoanCreationType} />
                                }
                                stepInstruction={<PrefixTrans>INSTRUCTION</PrefixTrans>}
                                phaseId={config.id}>
      {
        printsEnabled && <div className={styles.printApplication}>
          <Trans>{TRANS_PREFIX}.PRINT_ALL</Trans>
          <NxButton className={clsx(styles.printButton)}
                    startIcon={<PrintIcon />}
                    variant={NxButtonVariant.OUTLINED}
                    onClick={onPrint}
                    loaded={!isGenerating}>
            <Trans>COMMON.PRINT</Trans>
          </NxButton>
        </div>
      }
    </LoanApplicationStep>;
  };

  return isNil(config)
    ? <NxLoader />
    : <SummaryStep config={config} />;
}
